import { useEffect } from 'react';
import { useLatestRef } from '@frontend/state-ref-helpers';

export interface UseDuplicateSubmissionGuardArgs {
  /** Disable once the flow is no longer on the fillable form (e.g. after this tab's own successful submit). */
  enabled: boolean;
  isSubmitted: boolean;
  refetch: () => void;
  onAlreadySubmitted?: () => void;
}

/**
 * Guards against multi-tab duplicate submissions: re-checks the form's submitted status whenever the
 * tab regains visibility, and routes the user to the "already submitted" state if another tab beat them to it.
 */
export const useDuplicateSubmissionGuard = ({
  enabled,
  isSubmitted,
  refetch,
  onAlreadySubmitted,
}: UseDuplicateSubmissionGuardArgs) => {
  const onAlreadySubmittedRef = useLatestRef(onAlreadySubmitted);

  useEffect(() => {
    if (!enabled) return undefined;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refetch();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled, refetch]);

  useEffect(() => {
    if (enabled && isSubmitted) {
      onAlreadySubmittedRef.current?.();
    }
  }, [enabled, isSubmitted, onAlreadySubmittedRef]);
};
